import React from 'react';
import useImageAlts from '../hooks/useImageAlts';
import useTargetLocales from '../hooks/useTargetLocales';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import { Info } from 'lucide-react';
import SyncModal from '../components/SyncModal';
import { localeLabel } from '../utils/localeFlag';

const RESOURCE_TYPE_FILTERS = [
  { id: '', label: 'All images' },
  { id: 'MEDIA_IMAGE', label: 'Product images' },
  { id: 'COLLECTION_IMAGE', label: 'Collection images' },
  { id: 'ARTICLE_IMAGE', label: 'Article images' },
];

const POLICY_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'translate', label: 'Translate' },
  { id: 'skip', label: 'Skip (user)' },
  { id: 'auto-skipped', label: 'Auto-skipped' },
];

const STATUS_FILTERS = [
  { id: 'all', label: 'All images' },
  { id: 'missing', label: 'Missing translations only' },
];

const TYPE_LABEL = {
  MEDIA_IMAGE: 'Product',
  COLLECTION_IMAGE: 'Collection',
  ARTICLE_IMAGE: 'Article',
};

// Per-locale status badge — same mapping as the other inventory pages.
function StatusBadge({ status }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const map = {
    pending: { className: 'bg-gray-100 text-gray-800 hover:bg-gray-100', label: 'Pending' },
    in_batch: { className: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-100', label: 'In batch' },
    translated: { className: 'bg-blue-100 text-blue-800 hover:bg-blue-100', label: 'Translated' },
    pushed: { className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100', label: 'Pushed' },
    failed: { className: 'bg-red-100 text-red-800 hover:bg-red-100', label: 'Failed' },
  };
  const cfg = map[status] || { className: '', label: status };
  return <Badge className={cfg.className}>{cfg.label}</Badge>;
}

function truncate(value, n = 90) {
  if (!value) return '';
  return value.length > n ? value.slice(0, n) + '…' : value;
}

/**
 * Image alt-text inventory page (read-only status).
 *
 * Flat table — one row per image (every image has exactly one 'alt' field, so
 * none of ContentResources.jsx's prefix-grouping/field-expansion is needed).
 * Translation runs through the bulk image_alt CLI pipeline / nightly sync; this
 * page surfaces inventory + per-locale status and a "Sync from Shopify" button.
 * Per-locale status is the alt STRING's state (dedup-by-source) — all images
 * sharing an alt show the same status.
 */
export default function ImageAlts() {
  const {
    rows,
    total,
    loading,
    error,
    resourceType,
    setResourceType,
    policy,
    setPolicy,
    status,
    setStatus,
    q,
    setQ,
    page,
    setPage,
    pageSize,
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    fetchData,
  } = useImageAlts();

  const { locales } = useTargetLocales();

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSyncClose = () => {
    setSyncModalOpen(false);
    fetchData();
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-foreground">Image alt-text</h1>
        <Button onClick={() => setSyncModalOpen(true)}>Sync from Shopify</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {lastSynced ? `Last synced: ${new Date(lastSynced).toLocaleString()}` : 'Never synced'}
        {!loading && ` · ${total.toLocaleString()} image(s) with alt text`}
        {!loading && status === 'missing' && ' · filtered to images with at least one missing translation'}
      </p>

      <div className="rounded-md border border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-100 p-3 mb-6 text-sm flex gap-2 items-start">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <strong>Heads up:</strong> this is a read-only status view. Image alt text is translated by
          the bulk <code>image_alt</code> pipeline (CLI / nightly sync), not from this page. Most alts
          are <em>product</em> images — collection and article images rarely carry alt text. Per-locale
          status reflects the alt <em>string</em>'s state, which is shared across every image that uses it.
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={resourceType}
          onChange={(e) => setResourceType(e.target.value)}
          className="border border-input bg-background h-9 rounded-md px-2 text-sm"
        >
          {RESOURCE_TYPE_FILTERS.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>

        <div className="flex gap-1">
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.id}
              variant={status === f.id ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setStatus(f.id)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <div className="flex gap-1">
          {POLICY_FILTERS.map((f) => (
            <Button
              key={f.id}
              variant={policy === f.id ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setPolicy(f.id)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <Input
          type="text"
          placeholder="Search alt text or image id..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[200px]"
        />
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {loading && <p className="text-sm text-muted-foreground">Loading...</p>}

      {!loading && rows.length === 0 && (
        <div className="border rounded p-8 text-center text-muted-foreground">
          No image alt-text rows match the current filters. Try a sync if the table is empty.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Alt text</TableHead>
                <TableHead className="w-28">Type</TableHead>
                {locales.map((loc) => (
                  <TableHead key={loc} className="w-28">{localeLabel(loc)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <div className="font-medium" title={row.raw_value || ''}>
                      {truncate(row.raw_value, 90)}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">{row.resource_id}</div>
                    {(row.translation_policy === 'skip' || row.auto_skip_reason) && (
                      <span className="text-xs text-muted-foreground">
                        skipped{row.auto_skip_reason ? ` (${row.auto_skip_reason})` : ''}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-muted text-muted-foreground hover:bg-muted">
                      {TYPE_LABEL[row.resource_type] || row.resource_type}
                    </Badge>
                  </TableCell>
                  {locales.map((loc) => (
                    <TableCell key={loc}>
                      <StatusBadge status={row[`${loc}_status`]} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages} · {total.toLocaleString()} total
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <SyncModal
        open={syncModalOpen}
        onClose={handleSyncClose}
        endpoint="/api/transly/image-alts/sync/"
        statusEndpoint="/api/transly/image-alts/sync-status/"
        title="Syncing image alt-text"
      />
    </div>
  );
}
